import { FC, useCallback, useContext, useState, useEffect } from 'react';
import {
  Flex,
  Column,
  Text,
  Input,
  Button,
  ButtonGroup,
  Select,
} from '@/_components';
import { ScheduleLineItemProps } from './ScheduleLineItem.types';
import {
  deletePriorYearScheduleLineItem,
  deletePriorYearScheduleLineItemByItemId,
  updatePriorYearScheduleLineItem,
  updatePriorYearScheduleLineItemByItemId,
} from '@/_api/admin-prior-year-items';
import { UserContext } from '@/_contexts/UserProvider';

export const ScheduleLineItem: FC<ScheduleLineItemProps> = ({
  companyId,
  lineItem,
  selectableCategories,
  reloadScheduleLineItems,
}) => {
  const { token } = useContext(UserContext);

  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const [category, setCategory] = useState<string>(lineItem.category);
  const [value, setValue] = useState<string>(lineItem.value);

  useEffect(() => {
    setCategory(lineItem.category);
  }, [lineItem.category]);

  useEffect(() => {
    setValue(lineItem.value);
  }, [lineItem.value]);

  const cancel = useCallback(() => {
    setCategory(lineItem.category);
    setValue(lineItem.value);
    setEditing(false);
  }, [lineItem]);

  const save = useCallback(async () => {
    try {
      setSaving(true);
      if (companyId) {
        await updatePriorYearScheduleLineItem({
          companyId,
          lineItemId: lineItem.id,
          category,
          value,
          token,
        });
      } else {
        await updatePriorYearScheduleLineItemByItemId({
          lineItemId: lineItem.id,
          category,
          value,
          token,
        });
      }
    } catch (e) {
      console.error(e);
      setValue(lineItem.value);
    } finally {
      setSaving(false);
      setEditing(false);
    }
  }, [companyId, lineItem, token, category, value]);

  const startEdit = useCallback(() => {
    setEditing(true);
  }, []);

  const remove = useCallback(async () => {
    try {
      if (companyId) {
        await deletePriorYearScheduleLineItem({
          companyId,
          lineItemId: lineItem.id,
          token,
        });
      } else {
        await deletePriorYearScheduleLineItemByItemId({
          lineItemId: lineItem.id,
          token,
        });
      }
    } catch (e) {
      console.error(e);
    } finally {
      reloadScheduleLineItems();
    }
  }, [companyId, lineItem, token, reloadScheduleLineItems]);

  const isRemovable = lineItem.removable !== false;

  return (
    <Flex isBetween={true} isYCentered={true} gap={4}>
      <Column width={420}>
        {!editing ? (
          <Text>{category}</Text>
        ) : (
          <Select
            label="Category"
            isLabelHidden={true}
            items={selectableCategories?.map((category) => ({
              name: category.label,
              value: category.value,
            }))}
            selectedValue={category}
            onChange={(e) => setCategory(e.target.value)}
            isFullWidth
          />
        )}
      </Column>

      <Input
        label={category}
        isLabelHidden={true}
        value={value}
        isDisabled={!editing}
        onChange={(e) => setValue(e.target.value)}
      />

      <ButtonGroup isSameLine>
        {!editing ? (
          <>
            <Button
              value="Edit"
              isIconOnly
              intent="secondary"
              size="small"
              withTooltip
              iconLeft="pencil"
              onClick={startEdit}
            />
            {isRemovable && (
              <Button
                value="Remove"
                isIconOnly
                intent="danger-secondary"
                size="small"
                withTooltip
                iconLeft="trash"
                onClick={remove}
              />
            )}
          </>
        ) : (
          <>
            <Button
              value="Save"
              isIconOnly
              intent="secondary"
              size="small"
              withTooltip={true}
              iconLeft="check"
              onClick={save}
              isLoading={saving}
            />
            <Button
              value="Cancel"
              isIconOnly
              intent="danger-secondary"
              size="small"
              withTooltip={true}
              iconLeft="x"
              onClick={cancel}
              isDisabled={saving}
            />
          </>
        )}
      </ButtonGroup>
    </Flex>
  );
};
